/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Badge, Icon } from "./icons";
import { cx } from "./cx";

export type Column<Row> = {
  key: string;
  label: ReactNode;
  /** fr weight (default 1), or a CSS track like "84px". */
  width?: number | string;
  align?: "start" | "end";
  /** With align "end": tabular numbers, no wrap. */
  numeric?: boolean;
  render?: (row: Row) => ReactNode;
  className?: string;
};

/**
 * The 1a framed table: a white card, a 36px uppercase header on
 * `surface-soft`, rows of at least 48px with hairline dividers. On narrow
 * screens it scrolls sideways rather than squeezing.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isDimmed,
  minWidth,
  footer,
  empty,
  label,
  className,
}: {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  isDimmed?: (row: Row) => boolean;
  /** Default: columns × 118px, clamped to 520–940. */
  minWidth?: number;
  footer?: ReactNode;
  empty: ReactNode;
  label?: string;
  className?: string;
}) {
  const template = columns
    .map((column) => (typeof column.width === "string" ? column.width : `minmax(0,${column.width ?? 1}fr)`))
    .join(" ");
  const rowStyle = {
    gridTemplateColumns: template,
    minWidth: minWidth ?? Math.min(940, Math.max(520, columns.length * 118)),
  };
  const cellClass = (column: Column<Row>) =>
    cx("rd-cell", column.align === "end" && (column.numeric ? "rd-cell-num" : "rd-cell-end"), column.className);

  return (
    <div className={cx("rd-table", className)} role="table" aria-label={label}>
      <div className="rd-table-row rd-table-head" style={rowStyle} role="row">
        {columns.map((column) => (
          <div key={column.key} role="columnheader" className={cx("rd-cell", column.align === "end" && "rd-cell-num")}>
            {column.label}
          </div>
        ))}
      </div>
      {rows.length ? (
        rows.map((row) => (
          <div
            key={rowKey(row)}
            role="row"
            style={rowStyle}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={
              onRowClick
                ? (event) => {
                    if (event.key === "Enter") onRowClick(row);
                  }
                : undefined
            }
            className={cx("rd-table-row", onRowClick && "rd-clickable", isDimmed?.(row) && "rd-dimmed")}
          >
            {columns.map((column) => (
              <div key={column.key} role="cell" className={cellClass(column)}>
                {column.render ? column.render(row) : ((row as Record<string, ReactNode>)[column.key] ?? null)}
              </div>
            ))}
          </div>
        ))
      ) : (
        <div className={cx("rd-table-empty", typeof empty !== "string" && "rd-table-empty-node")}>{empty}</div>
      )}
      {footer ?? null}
    </div>
  );
}

/** First cell: a 28px badge (or media) beside the name and a meta line. */
export function ItemCell({
  badge,
  badgeSize = 28,
  media,
  name,
  meta,
}: {
  badge?: string;
  badgeSize?: number;
  media?: ReactNode;
  name: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div className="rd-item">
      {badge ? <Badge id={badge} size={badgeSize} /> : (media ?? null)}
      <div className="rd-cell-stack">
        <span className="rd-item-name" dir="auto">
          {name}
        </span>
        {meta ? (
          <span className="rd-cell-sub" dir="auto">
            {meta}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Pages shown: first, last, and one either side of the current one. */
function pageList(page: number, pages: number): Array<number | "gap"> {
  const nums = [...new Set([1, pages, page - 1, page, page + 1])]
    .filter((n) => n >= 1 && n <= pages)
    .sort((a, b) => a - b);
  const out: Array<number | "gap"> = [];
  nums.forEach((n, i) => {
    if (i && n - nums[i - 1] > 1) out.push("gap");
    out.push(n);
  });
  return out;
}

export type PaginationLabels = {
  /** "{from}–{to} of {total}" already formatted. */
  range: (from: number, to: number, total: number) => string;
  rows: string;
  previous: string;
  next: string;
  pages: string;
  rowsPerPage: string;
};

/** The 56px footer inside a table card. Controlled: the URL owns the page. */
export function TablePagination({
  total,
  page,
  pageSize,
  pageSizes = [10, 20, 50],
  onPage,
  onPageSize,
  labels,
  locale,
}: {
  total: number;
  /** 1-based. */
  page: number;
  pageSize: number;
  /** null hides the Rows picker. */
  pageSizes?: number[] | null;
  onPage: (page: number) => void;
  onPageSize?: (size: number) => void;
  labels: PaginationLabels;
  locale: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);
  const go = (n: number) => {
    if (n >= 1 && n <= pages && n !== page) onPage(n);
  };
  const number = (n: number) => n.toLocaleString(locale === "ar" ? "ar-EG" : "en-US");
  return (
    <div className="rd-table-foot rd-pager">
      <span className="rd-pager-start">
        <span className="rd-num">{labels.range(from, to, total)}</span>
        {pageSizes && onPageSize ? (
          <span className="rd-pager-sizes" role="group" aria-label={labels.rowsPerPage}>
            <span>{labels.rows}</span>
            {pageSizes.map((n) => (
              <button
                key={n}
                type="button"
                className="rd-pager-size"
                aria-pressed={n === pageSize}
                onClick={() => onPageSize(n)}
              >
                {number(n)}
              </button>
            ))}
          </span>
        ) : null}
      </span>
      <nav className="rd-pager-pages" aria-label={labels.pages}>
        <button type="button" className="rd-pager-step" aria-label={labels.previous} disabled={page <= 1} onClick={() => go(page - 1)}>
          <Icon icon={ChevronLeft} size={15} className="rd-flip-rtl" />
        </button>
        {pageList(page, pages).map((n, i) =>
          n === "gap" ? (
            <span key={`g${i}`} className="rd-pager-gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className="rd-pager-num"
              aria-current={n === page ? "page" : undefined}
              onClick={() => go(n)}
            >
              {number(n)}
            </button>
          ),
        )}
        <button type="button" className="rd-pager-step" aria-label={labels.next} disabled={page >= pages} onClick={() => go(page + 1)}>
          <Icon icon={ChevronRight} size={15} className="rd-flip-rtl" />
        </button>
      </nav>
    </div>
  );
}
